import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import { 
  Camera, 
  Flashlight, 
  FlashlightOff, 
  ArrowLeft,
  QrCode,
  CheckCircle,
  X
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { QRConfirmModal, QRPaymentModal } from '@/components/qrscannerpage'
import { qrApi, walletApi } from '@/services/api'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/lib/utils'

export function QRScannerPage() {
  const { t } = useTranslation()
  const [isScanning, setIsScanning] = useState(true)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [flashOn, setFlashOn] = useState(false)
  const [scannedData, setScannedData] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [qrCodeData, setQrCodeData] = useState<any>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [successData, setSuccessData] = useState<any>(null)
  const [manualQRInput, setManualQRInput] = useState('')
  const [qrHash, setQrHash] = useState<string | null>(null) // Store original hash for payment
  
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null)
  const scannedDataRef = useRef<string | null>(null)
  const startedOnceRef = useRef(false)
  const navigate = useNavigate()

  // Handle QR code scanning
  const handleQRScan = useCallback(async (data: string, isManual = false) => {
    if (!data || data === scannedDataRef.current) {
      return
    }
    
    scannedDataRef.current = data
    
    // For manual input, don't change isScanning state to avoid cleanup conflicts
    if (!isManual) {
      setScannedData(data)
      setIsScanning(false)
      
      // Stop scanning first
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop()
        } catch (err) {
          // Ignore stop errors
        }
      }
    } else {
      // For manual input, just set scanned data without affecting scanning state
      setScannedData(data)
    }
    
    try {
      // Parse QR code data - it can be a JSON string or plain text
      let qrData: any
      try {
        qrData = typeof data === 'string' ? JSON.parse(data) : data
      } catch {
        // If not JSON, it's likely a hash - try to lookup as merchant QR code first, then receive_money
        try {
          const response = await qrApi.scanQRCode(data)
          setQrCodeData(response.data.data)
          setShowPaymentModal(true)
          return
        } catch (lookupError: any) {
          // If merchant QR code lookup fails, try receive_money QR code lookup
          try {
            const receiveResponse = await walletApi.lookupReceiveQR(data)
            const qrData = receiveResponse.data?.data || receiveResponse.data
            setQrHash(data) // Store original hash
            setQrCodeData(qrData)
            setShowConfirmModal(true)
            return
          } catch (receiveError: any) {
            // Both lookups failed
            if (lookupError.response?.status === 404 && receiveError.response?.status === 404) {
              toast.error(t('qrScanner.qrCodeNotFound') || 'QR code not found. The QR code may have expired or is invalid.')
            } else {
              throw lookupError
            }
            return
          }
        }
      }
      
      // Check if it's a receive_money QR code
      if (qrData.type === 'receive_money') {
        // Store hash if available
        if (qrData.qr_hash) {
          setQrHash(qrData.qr_hash)
        }
        // Show confirmation modal for receive_money QR codes
        setQrCodeData(qrData)
        setShowConfirmModal(true)
      } else {
        // Try to scan as merchant QR code
        try {
          const response = await qrApi.scanQRCode(data)
          setQrCodeData(response.data.data)
          setShowPaymentModal(true)
        } catch (lookupError: any) {
          throw lookupError
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          t('qrScanner.invalidQR')
      toast.error(errorMessage)
      if (!isManual) {
        setIsScanning(true)
      }
      setScannedData(null)
      scannedDataRef.current = null
    }
  }, [t])

  // Helper para garantir que o container tem tamanho antes de iniciar
  const waitForVisible = (id: string) =>
    new Promise<void>((resolve) => {
      const el = document.getElementById(id)
      if (!el) return resolve()
      const ok = () => {
        const w = el.offsetWidth, h = el.offsetHeight
        if (w > 0 && h > 0) return true
        return false
      }
      if (ok()) return resolve()
      const ro = new ResizeObserver(() => {
        if (ok()) { ro.disconnect(); resolve() }
      })
      ro.observe(el)
      // backup timeout
      setTimeout(() => { ro.disconnect(); resolve() }, 500)
    })

  // Initialize QR scanner
  useEffect(() => {
    let html5QrCode: Html5Qrcode | null = null
    let isStarted = false
    let rafId: number | null = null

    const stopFallback = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = null
    }

    const startNativeDetectorFallback = () => {
      // Fallback com BarcodeDetector (Chrome/Android costuma ter)
      // Serve para confirmar que o vídeo tem qualidade e que há QR visível
      // Se detectar, chama o teu handleQRScan e pára tudo.
      // Não interfere com o html5-qrcode – corre em paralelo.
      // Se não existir, simplesmente não faz nada.
      // @ts-ignore
      if (!('BarcodeDetector' in window)) {
        return
      }
      // @ts-ignore
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
      const tick = async () => {
        try {
          const video = document.querySelector<HTMLVideoElement>('#qr-reader video')
          if (video && video.readyState >= 2) {
            const codes = await detector.detect(video)
            if (codes && codes.length) {
              await handleQRScan(codes[0].rawValue)
              return
            }
          }
        } catch (e) { /* ignore */ }
        rafId = requestAnimationFrame(tick)
      }
      rafId = requestAnimationFrame(tick)
    }

    const startScanning = async () => {
      try {
        if (!isScanning) {
          return
        }

        // Evitar instâncias duplicadas
        if (startedOnceRef.current || html5QrCodeRef.current) {
          return
        }

        startedOnceRef.current = true

        const qrCodeId = 'qr-reader'
        const container = document.getElementById(qrCodeId)
        if (container) container.innerHTML = ''

        // Garante que o container tem width/height > 0 antes de iniciar
        await waitForVisible(qrCodeId)

        html5QrCode = new Html5Qrcode(qrCodeId)
        html5QrCodeRef.current = html5QrCode

        // ⚠️ Usa constraints "ideais", não `exact`, e pede resolução decente
        const videoConstraints: MediaTrackConstraints = {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          // Alguns browsers suportam isto:
          advanced: [{ focusMode: 'continuous' } as any]
        }

        await html5QrCode.start(
          { facingMode: 'environment' }, // deixa o browser escolher a melhor traseira
          {
            fps: 30, // Aumentado para melhor detecção
            disableFlip: false, // Permitir flip pode ajudar
            // Remover formatsToSupport restritivo para permitir todos os formatos
            aspectRatio: window.innerWidth / Math.max(1, window.innerHeight),
            videoConstraints
          } as any,
          (decodedText) => {
            handleQRScan(decodedText)
          },
          () => {
            // Ignore parsing errors silently
          }
        )
        isStarted = true

        // Ajustes no <video> (iOS/Safari)
        const videoEl = document.querySelector<HTMLVideoElement>('#qr-reader video')
        if (videoEl) {
          videoEl.setAttribute('playsinline', '')
          videoEl.muted = true
          videoEl.autoplay = true
          // Força a ocupar o ecrã (evita "preto"/letterbox)
          videoEl.style.width = '100%'
          videoEl.style.height = '100%'
          videoEl.style.objectFit = 'cover'
        }

        // Fallback paralelo – se isto detetar primeiro, chama handleQRScan
        startNativeDetectorFallback()

        setHasPermission(true)
      } catch (error) {
        setHasPermission(false)
        startedOnceRef.current = false
        html5QrCodeRef.current = null
      }
    }

    if (isScanning) {
      startScanning()
    }

    return () => {
      stopFallback()
      if (html5QrCode && isStarted) {
        html5QrCode.stop()
          .catch(() => {
            // Ignore stop errors
          })
          .finally(() => {
            try { html5QrCode?.clear() } catch {}
            startedOnceRef.current = false
            html5QrCodeRef.current = null
            const c = document.getElementById('qr-reader')
            if (c) c.innerHTML = ''
          })
      } else {
        startedOnceRef.current = false
        html5QrCodeRef.current = null
      }
    }
  }, [isScanning, handleQRScan])

  // Handle confirm payment from receive_money QR
  const handleConfirmPayment = async () => {
    setIsProcessing(true)
    try {
      // Validate QR code data - use stored hash or qr_hash from data
      const hashToUse = qrHash || qrCodeData.qr_hash
      if (!qrCodeData.wallet_id || !hashToUse) {
        toast.error(t('qrScanner.invalidQRCode'))
        setShowConfirmModal(false)
        setIsScanning(true)
        setScannedData(null)
        setIsProcessing(false)
        return
      }

      // Process the payment via backend
      const response = await walletApi.processReceiveQR({
        qr_hash: hashToUse,
        wallet_id: qrCodeData.wallet_id,
        amount: parseFloat(qrCodeData.amount || '0'),
        currency: qrCodeData.currency || 'STN',
        description: qrCodeData.description || 'Payment via QR code'
      })
      
      // Show success modal
      const responseData: any = response.data?.data || response.data
      setSuccessData({
        amount: qrCodeData.amount,
        currency: qrCodeData.currency || 'STN',
        user_name: qrCodeData.user_name,
        description: qrCodeData.description || '',
        message: responseData?.message || t('qrScanner.paymentSuccess')
      })
      setShowConfirmModal(false)
      setShowSuccessModal(true)
    } catch (error: any) {
      // Map backend error messages to translated messages
      let errorMessage: string
      const apiError = error.response?.data?.error || error.response?.data?.message || ''
      
      if (apiError.toLowerCase().includes('insufficient balance') || 
          apiError.toLowerCase().includes('saldo insuficiente')) {
        errorMessage = t('qrScanner.insufficientBalance')
      } else if (apiError.toLowerCase().includes('wallet not found') || 
                 apiError.toLowerCase().includes('carteira não encontrada')) {
        errorMessage = t('qrScanner.walletNotFound')
      } else if (apiError.toLowerCase().includes('amount must be greater') || 
                 apiError.toLowerCase().includes('valor inválido')) {
        errorMessage = t('qrScanner.invalidAmount')
      } else if (apiError.toLowerCase().includes('cannot send to own wallet') || 
                 apiError.toLowerCase().includes('própria carteira')) {
        errorMessage = t('qrScanner.cannotSendToOwnWallet')
      } else if (apiError.toLowerCase().includes('currency') || 
                 apiError.toLowerCase().includes('moeda')) {
        errorMessage = t('qrScanner.invalidCurrency')
      } else if (apiError) {
        errorMessage = apiError
      } else {
        errorMessage = t('qrScanner.paymentError')
      }
      
      toast.error(errorMessage)
      setShowConfirmModal(false)
      setIsScanning(true)
      setScannedData(null)
    } finally {
      setIsProcessing(false)
    }
  }

  // Toggle flash
  const toggleFlash = () => {
    setFlashOn(!flashOn)
    // In a real implementation, you would control the camera flash here
  }

  // Close modals and restart scanning
  const handleCloseModal = () => {
    setShowConfirmModal(false)
    setShowPaymentModal(false)
    setShowSuccessModal(false)
    setQrCodeData(null)
    setSuccessData(null)
    setQrHash(null)
    setIsScanning(true)
    setScannedData(null)
    scannedDataRef.current = null
  }

  // Handle success modal close
  const handleSuccessClose = () => {
    setShowSuccessModal(false)
    setSuccessData(null)
    navigate('/dashboard')
  }

  // Handle successful payment
  const handlePaymentSuccess = () => {
    toast.success(t('success.paymentSuccessful'))
    handleCloseModal()
    navigate('/dashboard')
  }

  // Handle manual QR code input
  const handleManualQRSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!manualQRInput.trim()) {
      toast.error(t('qrScanner.pleaseEnterQRCode'))
      return
    }
    
    const qrData = manualQRInput.trim()
    setManualQRInput('')
    
    // Use requestAnimationFrame to avoid state transition conflicts
    requestAnimationFrame(() => {
      handleQRScan(qrData, true)
    })
  }

  if (hasPermission === false) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Camera className="h-8 w-8 text-error" />
          </div>
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            {t('qrScanner.cameraAccessRequired')}
          </h2>
          <p className="text-text-secondary mb-6">
            {t('qrScanner.cameraPermissionMessage')}
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            {t('common.backToHome')}
          </Button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 h-screen w-screen bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-white bg-black/20 backdrop-blur-sm"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {t('common.back')}
          </Button>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFlash}
              className="text-white bg-black/20 backdrop-blur-sm"
            >
              {flashOn ? <FlashlightOff className="h-5 w-5" /> : <Flashlight className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Camera View */}
      <div className="absolute inset-0 w-full h-full">
        <div id="qr-reader" className="absolute inset-0 w-full h-full" />
        
        {/* QR Scanner Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="qr-scanner-overlay">
          </div>
        </div>

        {/* Instructions */}
        <div className="absolute bottom-32 left-0 right-0 z-10 p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-6">
              <QrCode className="h-8 w-8 mx-auto mb-3 text-primary" />
              <h3 className="text-lg font-semibold mb-2">
                {t('qrScanner.scanQRCode')}
              </h3>
              <p className="text-sm text-white/80">
                {t('qrScanner.positionQRCode')}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Manual QR Code Input */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-4 safe-area-inset-bottom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto"
          >
            <form onSubmit={handleManualQRSubmit} className="flex gap-2">
              <input
                type="text"
                value={manualQRInput}
                onChange={(e) => setManualQRInput(e.target.value)}
                placeholder={t('qrScanner.enterQRCodeManually') || 'Enter QR code manually'}
                className="flex-1 px-4 py-3 bg-white/90 backdrop-blur-sm rounded-xl text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button
                type="submit"
                className="px-6 bg-primary text-white rounded-xl hover:bg-primary/90"
              >
                <CheckCircle className="h-5 w-5" />
              </Button>
            </form>
          </motion.div>
        </div>
      </div>

      {/* Confirm Modal for receive_money QR codes */}
      {showConfirmModal && qrCodeData && (
        <QRConfirmModal
          qrData={qrCodeData}
          onClose={handleCloseModal}
          onConfirm={handleConfirmPayment}
          isProcessing={isProcessing}
        />
      )}

      {/* Payment Modal for merchant QR codes */}
      {showPaymentModal && qrCodeData && (
        <QRPaymentModal
          qrCodeData={qrCodeData}
          onClose={handleCloseModal}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && successData && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleSuccessClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-success" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-text-primary mb-4">
                  {t('qrScanner.paymentSuccess')}
                </h2>
                
                {/* Payment Details */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-text-secondary">{t('common.amount')}:</span>
                    <span className="font-semibold text-text-primary text-lg">
                      {formatCurrency(parseFloat(successData.amount), successData.currency)}
                    </span>
                  </div>
                  
                  {successData.user_name && (
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">{t('common.to')}:</span>
                      <span className="font-semibold text-text-primary">
                        {successData.user_name}
                      </span>
                    </div>
                  )}
                  
                  {successData.description && (
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-sm text-text-secondary text-left mb-1">
                        {t('common.description')}:
                      </p>
                      <p className="text-sm text-text-primary text-left italic">
                        "{successData.description}"
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <Button
                onClick={handleSuccessClose}
                className="w-full"
              >
                {t('common.close')}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}
